import { useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import FormField from "layouts/pages/account/components/FormField";
import type { FornecedorFinanceiroApi } from "../types/contas-pagar.types";
import {
  CONTAS_FIN_ACCENT,
  fieldSx,
} from "../../components/contas-financeiras.theme";
import NovoFornecedorDialog from "./NovoFornecedorDialog";

type Opcao = { value: string; label: string };

export type FornecedorAutocompleteFieldProps = {
  fornecedores: FornecedorFinanceiroApi[];
  value: string | null;
  onChange: (fornecedorId: string | null) => void;
  onRefetchFornecedores?: () => void;
  label?: string;
  disabled?: boolean;
  size?: "small" | "medium";
};

export default function FornecedorAutocompleteField({
  fornecedores,
  value,
  onChange,
  onRefetchFornecedores,
  label = "Fornecedor",
  disabled = false,
  size = "medium",
}: FornecedorAutocompleteFieldProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [extra, setExtra] = useState<FornecedorFinanceiroApi[]>([]);

  const opcoes = useMemo(() => {
    const map = new Map<string, Opcao>();
    for (const f of [...fornecedores, ...extra]) {
      if (!map.has(f.id)) {
        map.set(f.id, { value: f.id, label: f.nome });
      }
    }
    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, "pt-BR"),
    );
  }, [fornecedores, extra]);

  const selected = opcoes.find((o) => o.value === value) ?? null;

  const handleCreated = (fornecedor: FornecedorFinanceiroApi) => {
    setExtra((prev) =>
      prev.some((f) => f.id === fornecedor.id) ? prev : [...prev, fornecedor],
    );
    onChange(fornecedor.id);
    onRefetchFornecedores?.();
  };

  return (
    <>
      <Box
        display="flex"
        gap={1}
        alignItems="center"
        width="100%"
        sx={{ "& .MuiAutocomplete-root": { flex: 1, minWidth: 0 } }}
      >
        <Autocomplete
          sx={{ flex: 1, minWidth: 0, width: "100%" }}
          size={size}
          disabled={disabled}
          options={opcoes}
          value={selected}
          onChange={(_, v) => onChange(v?.value ?? null)}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          noOptionsText="Nenhum fornecedor cadastrado"
          renderInput={(params) => (
            <FormField
              {...params}
              label={label}
              placeholder="Selecione ou cadastre novo"
              sx={fieldSx}
              InputProps={{
                ...params.InputProps,
                style: { height: size === "small" ? 40 : 44 },
              }}
            />
          )}
        />
        <MDButton
          variant="outlined"
          color="dark"
          size="small"
          onClick={() => setDialogOpen(true)}
          disabled={disabled}
          sx={{
            flexShrink: 0,
            height: size === "small" ? 40 : 44,
            minWidth: size === "small" ? 40 : 44,
            px: 0,
            borderColor: CONTAS_FIN_ACCENT,
            color: CONTAS_FIN_ACCENT,
            "&:hover": {
              borderColor: CONTAS_FIN_ACCENT,
              bgcolor: "rgba(249, 96, 48, 0.06)",
            },
          }}
          aria-label="Cadastrar novo fornecedor"
        >
          <Icon fontSize="small">add</Icon>
        </MDButton>
      </Box>

      <NovoFornecedorDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}
